import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Buildings,
  House,
  MagnifyingGlass,
  Lightning,
  Drop,
  Flame,
  Package,
} from '@phosphor-icons/react';
import type { MapperAsset, TableField } from './MapperNodeCard';

const TYPE_ICONS: Record<string, any> = {
  'Electriciteit': Lightning,
  'Elektriciteit': Lightning,
  'Gas': Flame,
  'Water': Drop,
};

interface ParentPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (parentId: string | null) => void;
  asset: MapperAsset | null;
  candidates: MapperAsset[];
  tableFields: TableField[];
  isUpdating: boolean;
}

const ParentPickerDialog: React.FC<ParentPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  asset,
  candidates,
  tableFields,
  isUpdating,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return candidates;
    const q = search.toLowerCase();
    return candidates.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.typeName.toLowerCase().includes(q) ||
      c.unitId?.toLowerCase().includes(q) ||
      Object.values(c.fieldValues || {}).some(v =>
        String(v).toLowerCase().includes(q)
      )
    );
  }, [candidates, search]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Selecteer bovenliggende voorziening</DialogTitle>
          <DialogDescription>
            {asset ? `Kies een ouder voor "${asset.name}"` : 'Selecteer een voorziening'}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek op naam, type, unit..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {asset?.parentAssetId && (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive border-destructive/30 hover:bg-destructive/10"
            onClick={() => onSelect(null)}
            disabled={isUpdating}
          >
            Ontkoppelen van huidige ouder
          </Button>
        )}

        <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Geen voorzieningen gevonden
            </p>
          ) : (
            filtered.map(c => {
              const Icon = TYPE_ICONS[c.typeName] || Package;
              const isCurrentParent = asset?.parentAssetId === c.id;

              return (
                <button
                  key={c.id}
                  className={`w-full text-left rounded-lg border p-3 transition-colors hover:bg-accent/50 ${
                    isCurrentParent ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => onSelect(c.id)}
                  disabled={isUpdating}
                >
                  <div className="flex items-start gap-2">
                    <Icon className="h-4 w-4 mt-0.5 text-foreground/60" weight="duotone" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{c.name}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">{c.typeName}</Badge>
                        {isCurrentParent && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">huidige ouder</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        {c.unitId ? (
                          <>
                            <House className="h-3 w-3" />
                            <span>{c.unitId}</span>
                          </>
                        ) : (
                          <>
                            <Buildings className="h-3 w-3" />
                            <span>Gebouw-niveau</span>
                          </>
                        )}
                      </div>
                      {tableFields.length > 0 && (
                        <div className="flex flex-wrap gap-x-3 gap-y-0 mt-1">
                          {tableFields.map(f => {
                            const val = c.fieldValues?.[f.fieldName];
                            if (val == null || val === '') return null;
                            return (
                              <span key={f.fieldName} className="text-[11px] text-muted-foreground">
                                <span className="font-medium text-foreground/70">{f.displayName}:</span> {String(val)}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ParentPickerDialog;
